import React, { useState, useEffect } from 'react';
import { Form, Input, Button, Card, message, DatePicker, InputNumber, Select, Row, Col, Switch, Tag, Checkbox } from 'antd';
import { useNavigate, useParams } from 'react-router-dom';
import { SaveOutlined, ArrowLeftOutlined, PlusOutlined, CloseOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import * as specialAlertConditionApi from '../../../services/specialAlertConditionApi';

const { Option } = Select;

const prefectures = [
  '北海道', '青森県', '岩手県', '宮城県', '秋田県', '山形県', '福島県',
  '茨城県', '栃木県', '群馬県', '埼玉県', '千葉県', '東京都', '神奈川県',
  '新潟県', '富山県', '石川県', '福井県', '山梨県', '長野県',
  '岐阜県', '静岡県', '愛知県', '三重県',
  '滋賀県', '京都府', '大阪府', '兵庫県', '奈良県', '和歌山県',
  '鳥取県', '島根県', '岡山県', '広島県', '山口県',
  '徳島県', '香川県', '愛媛県', '高知県',
  '福岡県', '佐賀県', '長崎県', '熊本県', '大分県', '宮崎県', '鹿児島県', '沖縄県'
];

const layoutOptions = [
  '1K', '1DK', '1LDK',
  '2K', '2DK', '2LDK',
  '3K', '3DK', '3LDK',
  '4K', '4DK', '4LDK',
  '5LDK',
];

interface AreaEntry {
  prefecture: string;
  district: string;
}

const SpecialNotificationPropertyForm: React.FC = () => {
  const [form] = Form.useForm();
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const [loading, setLoading] = useState(false);
  const [submitLoading, setSubmitLoading] = useState(false);
  const [areas, setAreas] = useState<AreaEntry[]>([{ prefecture: '', district: '' }]);
  const [noteKeywords, setNoteKeywords] = useState<string[]>([]);
  const [newKeyword, setNewKeyword] = useState('');
  const isEditMode = id !== 'new' && id !== undefined;

  useEffect(() => {
    if (isEditMode && id) {
      loadData();
    } else {
      form.setFieldsValue({
        registered_at: dayjs(),
        is_active: true,
      });
    }
  }, [id, isEditMode, form]);

  const loadData = async () => {
    try {
      setLoading(true);
      const data = await specialAlertConditionApi.getById(Number(id));

      form.setFieldsValue({
        ...data,
        registered_at: data.registered_at ? dayjs(data.registered_at) : null,
        layout_types: data.layout_types || [],
      });

      if (data.areas && data.areas.length > 0) {
        setAreas(data.areas);
      }
      if (data.note_keywords && data.note_keywords.length > 0) {
        setNoteKeywords(data.note_keywords);
      }
    } catch (error) {
      console.error('Failed to load data:', error);
      message.error('データの読み込みに失敗しました');
    } finally {
      setLoading(false);
    }
  };

  const handleAddArea = () => {
    setAreas([...areas, { prefecture: '', district: '' }]);
  };

  const handleRemoveArea = (index: number) => {
    if (areas.length > 1) {
      setAreas(areas.filter((_, i) => i !== index));
    }
  };

  const handleAreaChange = (index: number, field: keyof AreaEntry, value: string) => {
    const newAreas = [...areas];
    newAreas[index] = { ...newAreas[index], [field]: value };
    setAreas(newAreas);
  };

  const handleAddKeyword = () => {
    if (newKeyword.trim() && !noteKeywords.includes(newKeyword.trim())) {
      setNoteKeywords([...noteKeywords, newKeyword.trim()]);
      setNewKeyword('');
    }
  };

  const handleRemoveKeyword = (keyword: string) => {
    setNoteKeywords(noteKeywords.filter(k => k !== keyword));
  };

  const handleSubmit = async (values: any) => {
    try {
      setSubmitLoading(true);

      const validAreas = areas.filter(a => a.prefecture && a.district);
      if (validAreas.length === 0) {
        message.error('エリアを1つ以上指定してください');
        return;
      }

      const formattedValues = {
        ...values,
        registered_at: values.registered_at ? values.registered_at.format('YYYY-MM-DD') : null,
        areas: validAreas,
        note_keywords: noteKeywords.length > 0 ? noteKeywords : null,
      };

      const result = isEditMode && id
        ? await specialAlertConditionApi.update(Number(id), formattedValues)
        : await specialAlertConditionApi.create(formattedValues);

      if (result.validationErrors) {
        const fieldErrors = Object.entries(result.validationErrors).map(
          ([name, messages]) => ({
            name,
            errors: messages,
          })
        );
        form.setFields(fieldErrors);
        message.error('入力内容にエラーがあります。各項目を確認してください。');
        return;
      }

      message.success(isEditMode ? '更新しました' : '登録しました');
      navigate('/notification-properties');
    } catch (error) {
      console.error('Failed to submit:', error);
      message.error(isEditMode ? '更新に失敗しました' : '登録に失敗しました');
    } finally {
      setSubmitLoading(false);
    }
  };

  return (
    <div className="dark-theme-container">
      <div style={{ marginBottom: 24 }}>
        <Button
          icon={<ArrowLeftOutlined />}
          onClick={() => navigate('/notification-properties')}
          style={{ marginBottom: 16 }}
        >
          一覧に戻る
        </Button>
        <h1 className="dark-title">{isEditMode ? '特別条件編集' : '特別条件新規登録'}</h1>
      </div>

      <Card loading={loading}>
        <Form
          form={form}
          layout="vertical"
          onFinish={handleSubmit}
          autoComplete="off"
        >
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                label="登録タイトル"
                name="title"
                rules={[{ required: true, message: '登録タイトルを入力してください' }]}
              >
                <Input placeholder="例：☆特別条件 - 戸田市/練馬区" size="large" />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item label="登録日" name="registered_at">
                <DatePicker
                  style={{ width: '100%' }}
                  format="YYYY年MM月DD日"
                  placeholder="登録日を選択"
                  size="large"
                />
              </Form.Item>
            </Col>
          </Row>

          {/* エリア（複数指定可） */}
          <div style={{ marginBottom: 24 }}>
            <label style={{ fontWeight: 'bold', display: 'block', marginBottom: 8 }}>
              エリア（複数指定可）
            </label>
            {areas.map((area, index) => (
              <Row gutter={8} key={index} style={{ marginBottom: 8 }}>
                <Col span={8}>
                  <Select
                    value={area.prefecture || undefined}
                    placeholder="都道府県"
                    size="large"
                    showSearch
                    onChange={(val) => handleAreaChange(index, 'prefecture', val)}
                  >
                    {prefectures.map(pref => (
                      <Option key={pref} value={pref}>{pref}</Option>
                    ))}
                  </Select>
                </Col>
                <Col span={10}>
                  <Input
                    value={area.district}
                    placeholder="市区町村（例：練馬区）"
                    size="large"
                    onChange={(e) => handleAreaChange(index, 'district', e.target.value)}
                  />
                </Col>
                <Col span={6}>
                  {areas.length > 1 && (
                    <Button
                      danger
                      icon={<CloseOutlined />}
                      onClick={() => handleRemoveArea(index)}
                      size="large"
                    >
                      削除
                    </Button>
                  )}
                </Col>
              </Row>
            ))}
            <Button
              type="dashed"
              onClick={handleAddArea}
              icon={<PlusOutlined />}
              style={{ marginTop: 8 }}
            >
              エリアを追加
            </Button>
          </div>

          <Form.Item
            label="駅徒歩（○分以内）"
            name="walk_minutes_max"
          >
            <InputNumber
              style={{ width: '100%' }}
              placeholder="例：5"
              min={1}
              max={60}
              size="large"
              addonAfter="分以内"
            />
          </Form.Item>

          <Form.Item
            label="間取り（複数選択可）"
            name="layout_types"
          >
            <Checkbox.Group options={layoutOptions} />
          </Form.Item>

          <Form.Item label="面積（㎡）" style={{ marginBottom: 0 }}>
            <Row gutter={8}>
              <Col span={11}>
                <Form.Item name="area_m2_min">
                  <InputNumber style={{ width: '100%' }} placeholder="最小" min={0} step={0.01} size="large" />
                </Form.Item>
              </Col>
              <Col span={2} style={{ textAlign: 'center', lineHeight: '40px' }}>
                <span style={{ fontSize: '16px' }}>～</span>
              </Col>
              <Col span={11}>
                <Form.Item name="area_m2_max">
                  <InputNumber style={{ width: '100%' }} placeholder="最大" min={0} step={0.01} size="large" />
                </Form.Item>
              </Col>
            </Row>
          </Form.Item>

          <Form.Item
            label="築年数（○年以内）"
            name="built_year_max"
          >
            <InputNumber
              style={{ width: '100%' }}
              placeholder="例：30"
              min={0}
              max={200}
              size="large"
              addonAfter="年以内"
            />
          </Form.Item>

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                label="階数（○階以上）"
                name="floor_min"
              >
                <InputNumber
                  style={{ width: '100%' }}
                  placeholder="例：2"
                  min={1}
                  size="large"
                  addonAfter="階以上"
                />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                label="希望階（参考）"
                name="floor_preferred"
                tooltip="通知フィルタリングには使用されません。条件メモ用です。"
              >
                <InputNumber
                  style={{ width: '100%' }}
                  placeholder="例：3"
                  min={1}
                  size="large"
                  addonAfter="階"
                />
              </Form.Item>
            </Col>
          </Row>

          <Form.Item label="総額（万円）" style={{ marginBottom: 0 }}>
            <Row gutter={8}>
              <Col span={11}>
                <Form.Item name="price_total_min">
                  <InputNumber
                    style={{ width: '100%' }}
                    placeholder="最小"
                    min={0}
                    size="large"
                    formatter={value => value ? `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',') : ''}
                    parser={value => value!.replace(/,/g, '') as any}
                  />
                </Form.Item>
              </Col>
              <Col span={2} style={{ textAlign: 'center', lineHeight: '40px' }}>
                <span style={{ fontSize: '16px' }}>～</span>
              </Col>
              <Col span={11}>
                <Form.Item name="price_total_max">
                  <InputNumber
                    style={{ width: '100%' }}
                    placeholder="最大"
                    min={0}
                    size="large"
                    formatter={value => value ? `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',') : ''}
                    parser={value => value!.replace(/,/g, '') as any}
                  />
                </Form.Item>
              </Col>
            </Row>
          </Form.Item>

          <Form.Item label="坪単価（万円）" style={{ marginBottom: 0 }}>
            <Row gutter={8}>
              <Col span={11}>
                <Form.Item name="price_per_tsubo_min">
                  <InputNumber
                    style={{ width: '100%' }}
                    placeholder="最小"
                    min={0}
                    size="large"
                    formatter={value => value ? `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',') : ''}
                    parser={value => value!.replace(/,/g, '') as any}
                  />
                </Form.Item>
              </Col>
              <Col span={2} style={{ textAlign: 'center', lineHeight: '40px' }}>
                <span style={{ fontSize: '16px' }}>～</span>
              </Col>
              <Col span={11}>
                <Form.Item name="price_per_tsubo_max">
                  <InputNumber
                    style={{ width: '100%' }}
                    placeholder="最大"
                    min={0}
                    size="large"
                    formatter={value => value ? `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',') : ''}
                    parser={value => value!.replace(/,/g, '') as any}
                  />
                </Form.Item>
              </Col>
            </Row>
          </Form.Item>

          {/* 備考キーワード */}
          <div style={{ marginBottom: 24 }}>
            <label style={{ fontWeight: 'bold', display: 'block', marginBottom: 8 }}>
              備考キーワード（備考欄から検索）
            </label>
            <div style={{ marginBottom: 8 }}>
              {noteKeywords.map(keyword => (
                <Tag
                  key={keyword}
                  closable
                  onClose={() => handleRemoveKeyword(keyword)}
                  style={{ marginBottom: 4 }}
                >
                  {keyword}
                </Tag>
              ))}
            </div>
            <Row gutter={8}>
              <Col span={18}>
                <Input
                  value={newKeyword}
                  placeholder="例：リフォーム済"
                  size="large"
                  onChange={(e) => setNewKeyword(e.target.value)}
                  onPressEnter={handleAddKeyword}
                />
              </Col>
              <Col span={6}>
                <Button
                  onClick={handleAddKeyword}
                  icon={<PlusOutlined />}
                  size="large"
                  disabled={!newKeyword.trim()}
                >
                  追加
                </Button>
              </Col>
            </Row>
          </div>

          <Form.Item
            label="通知先メールアドレス"
            name="notification_email"
            tooltip="未入力の場合はデフォルト（m-furutachi@perva.co.jp）に送信されます"
            rules={[{ type: 'email', message: 'メールアドレスの形式が正しくありません' }]}
          >
            <Input
              placeholder="未入力でデフォルト通知先に送信"
              size="large"
              type="email"
            />
          </Form.Item>

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                label="ステータス"
                name="is_active"
                valuePropName="checked"
                tooltip="無効にすると検索・通知が停止されます"
              >
                <Switch checkedChildren="アクティブ" unCheckedChildren="無効" />
              </Form.Item>
            </Col>
          </Row>

          <Form.Item style={{ marginTop: 32 }}>
            <div style={{ display: 'flex', gap: 16, justifyContent: 'flex-end' }}>
              <Button size="large" onClick={() => navigate('/notification-properties')}>
                キャンセル
              </Button>
              <Button
                type="primary"
                htmlType="submit"
                loading={submitLoading}
                icon={<SaveOutlined />}
                size="large"
              >
                {isEditMode ? '更新' : '登録'}
              </Button>
            </div>
          </Form.Item>
        </Form>
      </Card>
    </div>
  );
};

export default SpecialNotificationPropertyForm;
